import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
  ExchangeRateCardComponent,
  ExchangeRateCardData,
} from '../exchange-rate/exchange-rate-card.component';
import { MonthlyExchangeDialogComponent } from '../exchange-rate/monthly-exchange/monthly-exchange-dialog.component';
import { ManageExchangeDialogComponent } from '../exchange-rate/manage-rate/manage-exchange-dialog.component';
import { ExchangeRate, ExchangeRateService } from 'src/app/features';

interface homecards {
  id: number;
  ruta: string;
  iconName: string;
  title: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatRippleModule,
    MatDialogModule,
    TablerIconsModule,
    MatButtonModule,
    ExchangeRateCardComponent,
  ],

  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit {
  homeCards: homecards[] = [
    {
      id: 1,
      ruta: '/clientes/ver-clientes',
      iconName: 'users',
      title: 'Clientes',
    },
    {
      id: 2,
      ruta: '/pagos-administrativos/proveedores',
      iconName: 'address-book',
      title: 'Proveedores ADM',
    },
    {
      id: 3,
      ruta: '/proveedores-sourcing/ver-proveedores',
      iconName: 'user-search',
      title: 'Proveedores SRC',
    },
    {
      id: 4,
      ruta: '/pagos-administrativos/listar-OC',
      iconName: 'shopping-cart',
      title: 'Orden de Compra',
    },
    {
      id: 5,
      ruta: '/pagos-administrativos/listar-RC',
      iconName: 'clipboard-list',
      title: 'Registro de Compra',
    },

    {
      id: 6,
      ruta: '/tesoreria/pagos-dia',
      iconName: 'calendar-check',
      title: 'Pagos del Día',
    },
    {
      id: 7,
      ruta: '/tesoreria/control-diario',
      iconName: 'clipboard-list',
      title: 'Control Diario',
    },
    {
      id: 8,
      ruta: '/tesoreria/conciliacion-bancaria',
      iconName: 'book',
      title: 'Conciliación Bancaria',
    },
    {
      id: 9,
      ruta: '/transacciones/cargar-movimientos-bancarios',
      iconName: 'file-upload',
      title: 'Cargar Mov. Bancario',
    },
    {
      id: 10,
      ruta: '/transacciones/movimientos-bancarios',
      iconName: 'credit-card-pay',
      title: 'Movimientos Bancarios',
    },
  ];

  exchangeRateCardData: ExchangeRateCardData = {
    title: 'Tipo de Cambio',
    lastUpdatedLabel: 'Sin definir',
    date: new Date(),
    buy: 0,
    sell: 0,
    closeBuy: 0,
    closeSell: 0,
  };

  isLoadingExchangeRate: boolean = false;
  isEmptyExchangeRate: boolean = false;

  constructor(
    private dialog: MatDialog,
    private exchangeRateService: ExchangeRateService,
  ) { }

  ngOnInit(): void {
    this.getLatestExchangeRate();
  }

  private calTimeAgo(date: Date): string {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    if (diffInMinutes < 1) {
      return 'justo ahora';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minuto(s) atrás`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hora(s) atrás`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} día(s) atrás`;
    }
  }

  private formatDateDisplay(date: Date): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  /**
   * Obtiene el último tipo de cambio disponible
   */
  private getLatestExchangeRate(): void {
    this.isLoadingExchangeRate = true;
    this.isEmptyExchangeRate = false;

    this.exchangeRateService.getLatest().subscribe(
      (data: {
        latestExchangeRate
        : ExchangeRate | null;
      }) => {
        if (data && data.latestExchangeRate) {
          const rate = data.latestExchangeRate;
          this.exchangeRateCardData = {
            ...this.exchangeRateCardData,
            date: new Date(`${rate.exchangeDate}T00:00:00`),
            buy: rate.buyRate,
            sell: rate.sellRate,
            closeBuy: rate.closingBuyRate,
            closeSell: rate.closingSellRate,
            lastUpdatedLabel: `${this.calTimeAgo(new Date(rate.updatedAt))}`,
          };
          this.isEmptyExchangeRate = false;
        } else {
          this.isEmptyExchangeRate = true;
        }
        this.isLoadingExchangeRate = false;
      },
      (error) => {
        console.error('Error fetching latest exchange rate:', error);
        this.isLoadingExchangeRate = false;
      }
    );
  }

  /**
   * Se activa cuando el usuario selecciona una fecha diferente
   */
  onExchangeDateChange(date: Date): void {
    this.isLoadingExchangeRate = true;
    this.isEmptyExchangeRate = false;
    this.exchangeRateCardData = { ...this.exchangeRateCardData, date };

    // Parsea la fecha a formato YYYY-MM-DD
    const formattedDate = date.toISOString().split('T')[0];

    this.exchangeRateService.getByDate(formattedDate).subscribe(
      (data: {
        exchangeRateByDate: ExchangeRate | null;
      }) => {
        if (data && data.exchangeRateByDate) {
          const rate = data.exchangeRateByDate;
          this.exchangeRateCardData = {
            ...this.exchangeRateCardData,
            buy: rate.buyRate,
            sell: rate.sellRate,
            closeBuy: rate.closingBuyRate,
            closeSell: rate.closingSellRate,
            lastUpdatedLabel: `${this.formatDateDisplay(new Date(`${rate.exchangeDate}T00:00:00`))}`,
          };
          this.isEmptyExchangeRate = false;
        } else {
          this.isEmptyExchangeRate = true;
        }
        this.isLoadingExchangeRate = false;
      },
      (error) => {
        console.error('Error fetching exchange rate:', error);
        this.isLoadingExchangeRate = false;
      }
    );
  }

  onOpenMonthlyExchange(): void {
    this.dialog.open(MonthlyExchangeDialogComponent, {
      width: '430px',
      height: '650px',
      data: { date: this.exchangeRateCardData.date },
    });
  }

  onManageExchange(): void {
    const dialogRef = this.dialog.open(ManageExchangeDialogComponent, {
      width: '400px',
      height: '550px',
      data: { date: this.exchangeRateCardData.date },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getLatestExchangeRate();
      }
    });
  }
}
